import {
  AddOutlined,
  ArchiveOutlined,
  DeleteForeverOutlined,
  EditOutlined,
  HubOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ProcessPointMasterData as ProcessPoint,
  ProcessPointMutation,
  archiveProcessPoint,
  createProcessPoint,
  deleteProcessPointPermanently,
  loadProcessPointAudit,
  loadProcessPointImpact,
  loadProcessPointMasterData,
  loadWarehouseStructure,
  updateProcessPoint
} from "./api";
import { ListRowActions } from "./ListRowActions";
import { PageHeader } from "./PageHeader";

const typeOptions = [
  "RECEIVING", "QUALITY_CONTROL", "QUARANTINE", "REPACKING",
  "PICKING_STAGING", "PACKING", "SHIPPING", "TRANSFER",
  "STAGING_AREA", "WORK_CENTER", "POU", "DELIVERY_POINT",
  "YARD_GATE", "DOOR", "DOCK", "DISCHARGING_POINT"
];
const capabilityOptions = [
  "RECEIVING", "QUALITY_INSPECTION", "REPACKAGING",
  "PUTAWAY_DEPARTURE", "PICKING_STAGING", "PACKING", "SHIPPING",
  "TRANSFER_DEPARTURE", "TRANSFER_ARRIVAL", "POU_DELIVERY"
];

interface Props {
  onBack: () => void;
}

const emptyForm: ProcessPointMutation = {
  warehouseId: "",
  code: "",
  displayName: "",
  type: "RECEIVING",
  linkedStorageBinId: null,
  travelSequence: null,
  priority: 100,
  capacityHu: null,
  putawaySourceEnabled: false,
  defaultForReceiving: false,
  defaultForQuality: false,
  defaultForRepacking: false,
  capabilities: []
};

export function ProcessPointMasterData({ onBack }: Props) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [search, setSearch] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [editing, setEditing] = useState<ProcessPoint | null>(null);
  const [creating, setCreating] = useState(false);
  const points = useQuery({
    queryKey: ["process-point-master-data"],
    queryFn: loadProcessPointMasterData
  });
  const structure = useQuery({
    queryKey: ["warehouse-structure"],
    queryFn: loadWarehouseStructure
  });
  const warehouses = (structure.data ?? []).filter((node) => node.type === "WAREHOUSE");
  const bins = useMemo(() => {
    const nodes = structure.data ?? [];
    const byId = new Map(nodes.map((node) => [node.id, node]));
    return nodes.filter((node) => node.type === "STORAGE_BIN").map((bin) => {
      let current = bin;
      while (current.parentId) {
        const parent = byId.get(current.parentId);
        if (!parent) break;
        if (parent.type === "WAREHOUSE") {
          return { ...bin, warehouseId: parent.id };
        }
        current = parent;
      }
      return { ...bin, warehouseId: "" };
    });
  }, [structure.data]);
  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("fr");
    return (points.data ?? []).filter((point) =>
      (!warehouseFilter || point.warehouseId === warehouseFilter) &&
      (!typeFilter || point.type === typeFilter) &&
      (!term || `${point.code} ${point.displayName} ${point.warehouseCode}`
        .toLocaleLowerCase("fr").includes(term))
    );
  }, [points.data, search, warehouseFilter, typeFilter]);

  return (
    <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
      <PageHeader compact title={t("masterData.processPoints.title")}
        onBack={onBack}
        actions={<Button variant="contained" startIcon={<AddOutlined />}
          onClick={() => setCreating(true)}>{t("masterData.common.add")}</Button>} />

      <Card sx={{ p: 1.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField fullWidth label={t("masterData.common.search")}
            placeholder={t("masterData.processPoints.searchPlaceholder")} value={search}
            onChange={(event) => setSearch(event.target.value)} />
          <FormControl fullWidth>
            <InputLabel>{t("masterData.common.warehouse")}</InputLabel>
            <Select label={t("masterData.common.warehouse")} value={warehouseFilter}
              onChange={(event) => setWarehouseFilter(event.target.value)}>
              <MenuItem value="">{t("masterData.common.all")}</MenuItem>
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} · {warehouse.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t("masterData.common.type")}</InputLabel>
            <Select label={t("masterData.common.type")} value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}>
              <MenuItem value="">{t("masterData.common.all")}</MenuItem>
              {typeOptions.map((type) => (
                <MenuItem key={type} value={type}>{t(`masterData.processPoints.types.${type}`)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Card>

      <Box sx={{ overflowY: "auto", minHeight: 0, pr: .5 }}>
        <Stack spacing={1}>
          {points.isError && <Alert severity="error">
            {t("masterData.processPoints.loadError")}
          </Alert>}
          {!points.isLoading && filtered.length === 0 && (
            <Alert severity="info">{t("masterData.processPoints.empty")}</Alert>
          )}
          {filtered.map((point) => (
            <Card key={point.id} sx={{ p: 1.5 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center"
                  sx={{ minWidth: 0 }}>
                  <HubOutlined color="secondary" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={800}>
                      {point.code} · {point.displayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {point.warehouseCode} · {t(`masterData.processPoints.types.${point.type}`, { defaultValue: point.type })}
                      {point.linkedStorageBinCode
                        ? ` · emplacement ${point.linkedStorageBinCode}` : ""}
                      {point.travelSequence
                        ? t("masterData.processPoints.sequenceValue", { value: point.travelSequence }) : ""}
                      {t("masterData.processPoints.priorityValue", { value: point.priority })}
                      {point.capacityHu ? t("masterData.processPoints.capacityValue", { value: point.capacityHu }) : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {point.capabilities.map((item) =>
                        t(`masterData.processPoints.capabilitiesMap.${item}`, { defaultValue: item })).join(" · ") || t("masterData.processPoints.noCapability")}
                    </Typography>
                  </Box>
                </Stack>
                <ListRowActions
                  actions={<Button variant="outlined" startIcon={<EditOutlined />}
                    onClick={() => setEditing(point)}>{t("masterData.common.manage")}</Button>}
                  status={<Chip label={t(`masterData.common.statuses.${point.status}`, { defaultValue: point.status })}
                    color={point.status === "ACTIVE" ? "success" : "default"} />} />
              </Stack>
            </Card>
          ))}
        </Stack>
      </Box>

      <ProcessPointDialog open={creating} point={null}
        warehouses={warehouses}
        bins={bins}
        onClose={() => setCreating(false)}
        onSaved={async () => {
          await client.invalidateQueries({ queryKey: ["process-point-master-data"] });
          setCreating(false);
        }} />
      <ProcessPointDialog open={editing !== null} point={editing}
        warehouses={warehouses}
        bins={bins}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          await client.invalidateQueries({ queryKey: ["process-point-master-data"] });
          setEditing(null);
        }} />
    </Stack>
  );
}

function ProcessPointDialog({
  open, point, warehouses, bins, onClose, onSaved
}: {
  open: boolean;
  point: ProcessPoint | null;
  warehouses: Array<{ id: string; code: string; displayName: string }>;
  bins: Array<{
    id: string;
    parentId: string | null;
    code: string;
    displayName: string;
    warehouseId: string;
  }>;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ProcessPointMutation>(emptyForm);
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!open) return;
    setForm(point ? fromPoint(point) : { ...emptyForm, capabilities: [] });
    setReason("");
  }, [open, point]);
  const effectiveForm = form;
  const impact = useQuery({
    queryKey: ["process-point-impact", point?.id],
    queryFn: () => loadProcessPointImpact(point!.id),
    enabled: open && point !== null
  });
  const audit = useQuery({
    queryKey: ["process-point-audit", point?.id],
    queryFn: () => loadProcessPointAudit(point!.id),
    enabled: open && point !== null
  });
  const mutation = useMutation({
    mutationFn: () => point
      ? updateProcessPoint(point.id, {
          ...effectiveForm, version: point.version, reason
        })
      : createProcessPoint(effectiveForm),
    onSuccess: onSaved
  });
  const archive = useMutation({
    mutationFn: () => archiveProcessPoint(point!.id, point!.version, reason),
    onSuccess: onSaved
  });
  const permanentDelete = useMutation({
    mutationFn: () => deleteProcessPointPermanently(
      point!.id, point!.version, reason
    ),
    onSuccess: onSaved
  });
  const selectedWarehouse = effectiveForm.warehouseId ?? "";
  const availableBins = bins.filter(
    (bin) => bin.warehouseId === selectedWarehouse
  );
  const error = mutation.error ?? archive.error ?? permanentDelete.error;

  function update<K extends keyof ProcessPointMutation>(
    key: K, value: ProcessPointMutation[K]
  ) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Box component="form" onSubmit={submit}>
        <DialogTitle>{point ? t("masterData.processPoints.editTitle", { code: point.code }) : t("masterData.processPoints.addTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: .5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <FormControl fullWidth>
                <InputLabel>{t("masterData.common.warehouse")}</InputLabel>
                <Select label={t("masterData.common.warehouse")} value={selectedWarehouse}
                  disabled={Boolean(point)}
                  onChange={(event) => update("warehouseId", event.target.value)}>
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.code} · {warehouse.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField fullWidth required label={t("masterData.common.code")}
                disabled={Boolean(point)} value={effectiveForm.code ?? ""}
                onChange={(event) => update("code", event.target.value)} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField fullWidth required label={t("masterData.common.name")}
                value={effectiveForm.displayName}
                onChange={(event) => update("displayName", event.target.value)} />
              <FormControl fullWidth>
                <InputLabel>{t("masterData.common.type")}</InputLabel>
                <Select label={t("masterData.common.type")} value={effectiveForm.type}
                  onChange={(event) => update("type", event.target.value)}>
                  {typeOptions.map((type) => (
                    <MenuItem key={type} value={type}>{t(`masterData.processPoints.types.${type}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <FormControl fullWidth>
              <InputLabel>{t("masterData.processPoints.physicalLocation")}</InputLabel>
              <Select label={t("masterData.processPoints.physicalLocation")}
                value={effectiveForm.linkedStorageBinId ?? ""}
                onChange={(event) => update(
                  "linkedStorageBinId", event.target.value || null
                )}>
                <MenuItem value="">{t("masterData.common.none")}</MenuItem>
                {availableBins.map((bin) => (
                  <MenuItem key={bin.id} value={bin.id}>
                    {bin.code} · {bin.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField fullWidth type="number"
                label={t("masterData.processPoints.movementSequence")}
                helperText={t("masterData.processPoints.movementHelp")}
                value={effectiveForm.travelSequence ?? ""}
                inputProps={{ min: 1 }}
                onChange={(event) => update(
                  "travelSequence",
                  event.target.value ? Number(event.target.value) : null
                )} />
              <TextField fullWidth type="number" label={t("masterData.processPoints.priority")}
                value={effectiveForm.priority}
                onChange={(event) => update("priority", Number(event.target.value))} />
              <TextField fullWidth type="number" label={t("masterData.processPoints.huCapacity")}
                value={effectiveForm.capacityHu ?? ""}
                onChange={(event) => update(
                  "capacityHu", event.target.value ? Number(event.target.value) : null
                )} />
            </Stack>
            <FormControl fullWidth>
              <InputLabel>{t("masterData.processPoints.capabilities")}</InputLabel>
              <Select multiple label={t("masterData.processPoints.capabilities")}
                value={effectiveForm.capabilities}
                onChange={(event) => update(
                  "capabilities",
                  typeof event.target.value === "string"
                    ? event.target.value.split(",") : event.target.value
                )}
                renderValue={(selected) => selected.map((item) =>
                  t(`masterData.processPoints.capabilitiesMap.${item}`, { defaultValue: item })).join(", ")}>
                {capabilityOptions.map((capability) => (
                  <MenuItem key={capability} value={capability}>
                    <Checkbox checked={effectiveForm.capabilities.includes(capability)} />
                    {t(`masterData.processPoints.capabilitiesMap.${capability}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel control={<Checkbox
              checked={effectiveForm.putawaySourceEnabled}
              onChange={(event) => update(
                "putawaySourceEnabled", event.target.checked
              )} />} label={t("masterData.processPoints.putawayStart")} />
            {effectiveForm.putawaySourceEnabled && (
              <Stack direction={{ xs: "column", sm: "row" }}>
                <FormControlLabel control={<Checkbox
                  checked={effectiveForm.defaultForReceiving}
                  onChange={(event) => update(
                    "defaultForReceiving", event.target.checked
                  )} />} label={t("masterData.processPoints.defaultReceipt")} />
                <FormControlLabel control={<Checkbox
                  checked={effectiveForm.defaultForQuality}
                  onChange={(event) => update(
                    "defaultForQuality", event.target.checked
                  )} />} label={t("masterData.processPoints.defaultQuality")} />
                <FormControlLabel control={<Checkbox
                  checked={effectiveForm.defaultForRepacking}
                  onChange={(event) => update(
                    "defaultForRepacking", event.target.checked
                  )} />} label={t("masterData.processPoints.defaultRepackaging")} />
              </Stack>
            )}
            {point && <TextField required label={t("masterData.common.reason")}
              value={reason} onChange={(event) => setReason(event.target.value)}
              helperText={t("masterData.common.requiredMin3")} />}
            {point && impact.data && (
              <Alert severity={impact.data.blockers.length ? "warning" : "info"}>
                {impact.data.blockers.length
                  ? impact.data.blockers.join(" · ")
                  : t("masterData.warehouse.historicalReferences", { count: impact.data.historicalReferenceCount })}
              </Alert>
            )}
            {point && audit.data && audit.data.length > 0 && (
              <Box>
                <Typography variant="subtitle2">Audit ({audit.data.length})</Typography>
                {audit.data.slice(0, 5).map((entry) => (
                  <Typography key={entry.id} variant="caption"
                    display="block" color="text.secondary">
                    {entry.action} · {entry.reason} · {entry.changedByDisplayName}
                  </Typography>
                ))}
              </Box>
            )}
            {error && <Alert severity="error">
              {error instanceof Error ? error.message : "Enregistrement impossible."}
            </Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          {point && point.status === "ACTIVE" && (
            <Button color="warning" startIcon={<ArchiveOutlined />}
              disabled={!impact.data?.archiveAllowed || reason.trim().length < 3}
              onClick={() => archive.mutate()}>{t("masterData.common.archive")}</Button>
          )}
          {point && (
            <Button color="error" startIcon={<DeleteForeverOutlined />}
              disabled={!impact.data?.permanentDeleteAllowed ||
                reason.trim().length < 3}
              onClick={() => permanentDelete.mutate()}>
              {t("masterData.common.deletePermanently")}
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={onClose}>{t("masterData.common.close")}</Button>
          <Button type="submit" variant="contained"
            disabled={!selectedWarehouse || !effectiveForm.code ||
              !effectiveForm.displayName ||
              (Boolean(point) && reason.trim().length < 3)}>
            {point ? t("masterData.common.save") : t("masterData.common.create")}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function fromPoint(point: ProcessPoint): ProcessPointMutation {
  return {
    warehouseId: point.warehouseId,
    code: point.code,
    displayName: point.displayName,
    type: point.type,
    linkedStorageBinId: point.linkedStorageBinId,
    travelSequence: point.travelSequence,
    priority: point.priority,
    capacityHu: point.capacityHu,
    putawaySourceEnabled: point.putawaySourceEnabled,
    defaultForReceiving: point.defaultForReceiving,
    defaultForQuality: point.defaultForQuality,
    defaultForRepacking: point.defaultForRepacking,
    capabilities: point.capabilities
  };
}
